import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
  Link,
} from "@react-pdf/renderer";

// =============================================================================
// ESTILOS DO DOCUMENTO PDF
// =============================================================================

const styles = StyleSheet.create({
  // Estrutura da página

  page: {
    fontSize: 11,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    position: "relative",
    padding: 40,
  },

  pageWithBackground: {
    fontSize: 11,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    position: "relative",
    paddingBottom: 105, // ~6cm do final
  },

  // Página com template personalizado (background)
  contentWithBackground: {
    paddingTop: 115, // ~6cm do topo (15% de 29.7cm = 4.455cm, arredondando para 85pt)

    paddingLeft: 40,
    paddingRight: 40,
    fontSize: 11,
    fontFamily: "Helvetica",
    position: "relative",
  },

  contentWithoutBackground: {
    padding: 10,
    fontSize: 11,
    fontFamily: "Helvetica",
    position: "relative",
  },

  /**
   * Espaçador fixo para páginas de continuação (wrap).
   * O react-pdf não reaplica o padding do container nas páginas seguintes (issue #733).
   * Este View fixo renderiza apenas nas páginas 2+ com a mesma altura do paddingTop,
   * mantendo contentWithBackground ou contentWithoutBackground visualmente consistentes.
   */
  continuationTopPaddingWithBg: {
    width: "100%",
    height: 115,
  },
  continuationTopPaddingWithoutBg: {
    width: "100%",
    height: 10,
  },

  // Background image
  backgroundImage: {
    position: "absolute",
    objectFit: "fill",
    width: "100%",
    height: "100vh",
  },

  // Container de conteúdo (fica acima do background).
  // Não usar height: "100%" para permitir que o conteúdo flua para páginas seguintes
  // quando ultrapassar o espaço disponível (page wrapping do react-pdf).
  contentContainer: {
    width: "100%",
    minHeight: "100%",
  },

  // Cabeçalho do documento
  header: {
    marginBottom: 10,
    paddingBottom: 10,
    borderBottom: "2 solid #00A884",
  },
  clinicName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#00A884",
    marginBottom: 4,
  },
  clinicInfo: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 2,
  },

  // Título do documento
  documentTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#333333",
    textAlign: "center",
    marginTop: 10,
    marginBottom: 8,
    textTransform: "uppercase",
  },

  // Data do atendimento
  attendanceDateSection: {
    textAlign: "right",
    marginBottom: 12,
    paddingRight: 5,
  },
  attendanceDateText: {
    fontSize: 10,
    color: "#666666",
  },
  attendanceDateValue: {
    fontSize: 11,
    color: "#333333",
    fontWeight: "bold",
  },

  // Seção de informações do paciente e tutor
  patientSection: {
    marginBottom: 15,
    borderRadius: 6,
    border: "1 solid #e0e0e0",
    overflow: "hidden",
  },

  // Cabeçalho das seções (Pet/Tutor)
  sectionHeader: {
    backgroundColor: "#00A884",
    padding: 6,
    flexDirection: "row",
    alignItems: "center",
  },
  sectionHeaderText: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#ffffff",
    textTransform: "uppercase",
  },

  // Container do conteúdo
  sectionContent: {
    backgroundColor: "#fafafa",
    padding: 10,
  },

  // Grid de informações
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  infoColumn: {
    flexDirection: "row",
    alignItems: "baseline",
    marginRight: 20,
    marginBottom: 4,
  },
  infoLabel: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#666666",
    marginRight: 4,
  },
  infoValue: {
    fontSize: 10,
    color: "#333333",
  },

  // Separador entre Pet e Tutor
  dividerLine: {
    height: 1,
    backgroundColor: "#e0e0e0",
  },

  // Seção de conteúdo
  contentSection: {
    marginBottom: 12,
  },
  contentTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#00A884",
    marginBottom: 6,
    textTransform: "uppercase",
  },
  contentText: {
    fontSize: 10,
    color: "#333333",
    lineHeight: 1.5,
    textAlign: "justify",
  },

  // Lista de itens (tratamentos, exames)
  itemContainer: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: "#fafafa",
    borderLeft: "3 solid #00A884",
  },
  itemTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#333333",
    marginBottom: 4,
  },
  itemDetail: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 2,
  },

  // Página do QR Code (página separada)
  qrCodePage: {
    padding: 40,
    fontSize: 11,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  qrCodeContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  qrCodeImage: {
    width: 250,
    height: 250,
    marginBottom: 20,
  },
  qrCodeTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#00A884",
    marginBottom: 10,
    textAlign: "center",
  },
  qrCodeLabel: {
    fontSize: 12,
    color: "#666666",
    textAlign: "center",
    marginBottom: 5,
  },
  qrCodeLink: {
    fontSize: 12,
    color: "#00A884",
    textAlign: "center",
    textDecoration: "underline",
    marginTop: 10,
    fontWeight: "bold",
  },
  qrCodeInstructions: {
    fontSize: 10,
    color: "#999999",
    textAlign: "center",
    marginTop: 20,
    maxWidth: 400,
    lineHeight: 1.6,
  },
});

// =============================================================================
// INTERFACES
// =============================================================================

interface Treatment {
  id: string;
  name: string;
  dosage: string;
  description: string;
}

interface Exam {
  id: string;
  description: string;
  notes: string;
}

interface Report {
  id: string;
  content: string;
}

interface PetInfo {
  name: string;
  species: string;
  breed: string;
  age?: string;
}

interface TutorInfo {
  name: string;
  document?: string;
  phone?: string;
}

interface VetDocumentTemplateProps {
  // Tipo do documento
  documentType: "treatment" | "exam" | "report" | "combined";

  // Dados do documento
  treatments?: Treatment[];
  exams?: Exam[];
  reports?: Report[];

  // Informações do pet e tutor
  petInfo: PetInfo;
  tutorInfo: TutorInfo;

  // Informações da clínica
  clinicName?: string;
  clinicAddress?: string;
  clinicPhone?: string;
  clinicCRMV?: string;

  // Informações do veterinário
  vetName?: string;
  vetCRMV?: string;

  // Data do atendimento
  attendanceDate?: string;

  // Template personalizado (imagem de background em base64)
  backgroundImage?: string;
  backgroundImageContentType?: string;

  // QR Code para validação do documento
  qrCodeBase64?: string;
  documentUUID?: string;
  linkDocument?: string;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Template de documento PDF para atendimento veterinário
 * Gera documentos em formato FHIR-compatível para impressão e assinatura
 */
export const VetDocumentTemplate: React.FC<VetDocumentTemplateProps> = ({
  documentType,
  treatments = [],
  exams = [],
  reports = [],
  petInfo,
  tutorInfo,
  clinicName = "Clínica Veterinária FluxVet",
  clinicAddress = "Endereço não informado",
  clinicPhone = "Telefone não informado",
  clinicCRMV = "CRMV não informado",
  vetName = "Veterinário(a) responsável",
  vetCRMV = "CRMV não informado",
  attendanceDate = new Date().toLocaleDateString("pt-BR"),
  backgroundImage,
  backgroundImageContentType,
  qrCodeBase64,
  documentUUID,
  linkDocument,
}) => {
  /**
   * Determina o título do documento baseado no tipo
   */
  const getDocumentTitle = (): string => {
    switch (documentType) {
      case "treatment":
        return "Receituário";
      case "exam":
        return "Solicitação de Exames/Procedimentos";
      case "report":
        return "Relatório de Atendimento";
      case "combined":
        return "Documentação Completa do Atendimento";
      default:
        return "Documento Veterinário";
    }
  };

  const backgroundImageSrc = backgroundImage
    ? `data:${backgroundImageContentType};base64,${backgroundImage}`
    : undefined;

  return (
    <Document>
      {/* wrap={true} (padrão) permite que o conteúdo flua para novas páginas automaticamente */}
      <Page
        size="A4"
        style={backgroundImage ? styles.pageWithBackground : styles.page}
        wrap
      >
        {/* ========== CONTAINER DE CONTEÚDO ========== */}
        <View style={styles.contentContainer}>
          {/* fixed: exibe o background em todas as páginas quando o conteúdo quebra */}
          {backgroundImage && (
            <Image
              src={backgroundImageSrc}
              style={styles.backgroundImage}
              fixed
            />
          )}

          {/* Workaround react-pdf #733: nas páginas de continuação (2+), reaplica o padding superior
              para manter contentWithBackground/contentWithoutBackground consistentes em todas as páginas */}
          <View
            fixed
            render={({ pageNumber }) =>
              pageNumber > 1 ? (
                <View
                  style={
                    backgroundImage
                      ? styles.continuationTopPaddingWithBg
                      : styles.continuationTopPaddingWithoutBg
                  }
                />
              ) : null
            }
          />

          <View
            style={
              backgroundImage
                ? styles.contentWithBackground
                : styles.contentWithoutBackground
            }
          >
            {/* ========== CABEÇALHO (apenas se não houver background) ========== */}

            <View style={styles.header}>
              {!backgroundImage && (
                <Text style={styles.clinicName}>{clinicName}</Text>
              )}

              <Text style={styles.clinicInfo}>{clinicAddress}</Text>

              {vetName && (
                <Text style={styles.clinicInfo}>
                  Veterinário(a): {vetName} | {vetCRMV}{" "}
                </Text>
              )}
              <Text style={styles.clinicInfo}>Telefone: {clinicPhone}</Text>
            </View>

            {/* ========== TÍTULO DO DOCUMENTO ========== */}
            <Text style={styles.documentTitle}>{getDocumentTitle()}</Text>

            {/* ========== DATA DO ATENDIMENTO ========== */}
            <View style={styles.attendanceDateSection}>
              <Text style={styles.attendanceDateText}>
                Data do Atendimento:
              </Text>
              <Text style={styles.attendanceDateValue}>{attendanceDate}</Text>
            </View>

            {/* ========== INFORMAÇÕES DO PACIENTE E TUTOR ========== */}
            <View style={styles.patientSection}>
              {/* Seção do Pet */}
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>🐾 Paciente</Text>
              </View>
              <View style={styles.sectionContent}>
                <View style={styles.infoGrid}>
                  <View style={styles.infoColumn}>
                    <Text style={styles.infoLabel}>Nome:</Text>
                    <Text style={styles.infoValue}>{petInfo.name}</Text>
                  </View>
                  <View style={styles.infoColumn}>
                    <Text style={styles.infoLabel}>Espécie:</Text>
                    <Text style={styles.infoValue}>{petInfo.species}</Text>
                  </View>
                  <View style={styles.infoColumn}>
                    <Text style={styles.infoLabel}>Raça:</Text>
                    <Text style={styles.infoValue}>{petInfo.breed}</Text>
                  </View>
                  {petInfo.age && (
                    <View style={styles.infoColumn}>
                      <Text style={styles.infoLabel}>Idade:</Text>
                      <Text style={styles.infoValue}>{petInfo.age}</Text>
                    </View>
                  )}
                </View>
              </View>

              {/* Divisor */}
              <View style={styles.dividerLine} />

              {/* Seção do Tutor */}
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>👤 Tutor</Text>
              </View>
              <View style={styles.sectionContent}>
                <View style={styles.infoGrid}>
                  <View style={styles.infoColumn}>
                    <Text style={styles.infoLabel}>Nome:</Text>
                    <Text style={styles.infoValue}>{tutorInfo.name}</Text>
                  </View>
                  {tutorInfo.document && (
                    <View style={styles.infoColumn}>
                      <Text style={styles.infoLabel}>CPF:</Text>
                      <Text style={styles.infoValue}>{tutorInfo.document}</Text>
                    </View>
                  )}
                  {tutorInfo.phone && (
                    <View style={styles.infoColumn}>
                      <Text style={styles.infoLabel}>Telefone:</Text>
                      <Text style={styles.infoValue}>{tutorInfo.phone}</Text>
                    </View>
                  )}
                </View>
              </View>
            </View>

            {/* ========== TRATAMENTOS ========== */}
            {treatments.length > 0 && (
              <View style={styles.contentSection}>
                <Text style={styles.contentTitle}>
                  Prescrição de Tratamentos
                </Text>
                {treatments.map((treatment, index) => (
                  <View
                    key={treatment.id}
                    style={styles.itemContainer}
                    wrap={false}
                  >
                    <Text style={styles.itemTitle}>
                      {index + 1}. {treatment.name}
                    </Text>
                    <Text style={styles.itemDetail}>
                      Posologia: {treatment.dosage}
                    </Text>
                    {treatment.description && (
                      <Text style={styles.itemDetail}>
                        Observações: {treatment.description}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* ========== EXAMES/PROCEDIMENTOS ========== */}
            {exams.length > 0 && (
              <View style={styles.contentSection}>
                <Text style={styles.contentTitle}>
                  Exames e Procedimentos Solicitados
                </Text>
                {exams.map((exam, index) => (
                  <View key={exam.id} style={styles.itemContainer} wrap={false}>
                    <Text style={styles.itemTitle}>
                      {index + 1}. {exam.description}
                    </Text>
                    {exam.notes && (
                      <Text style={styles.itemDetail}>
                        Observações: {exam.notes}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* ========== RELATÓRIOS ========== */}
            {reports.length > 0 && (
              <View style={styles.contentSection}>
                <Text style={styles.contentTitle}>Relatório Clínico</Text>
                {reports.map((report) => (
                  <View key={report.id} wrap={false}>
                    <Text style={styles.contentText}>{report.content}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </Page>

      {/* ========== PÁGINA DO QR CODE (separada) ========== */}
      {qrCodeBase64 && linkDocument && (
        <Page size="A4" style={styles.qrCodePage}>
          <View style={styles.qrCodeContainer}>
            <Text style={styles.qrCodeTitle}>Validação do Documento</Text>

            <Image
              src={`data:image/png;base64,${qrCodeBase64}`}
              style={styles.qrCodeImage}
            />

            <Text style={styles.qrCodeLabel}>
              Escaneie o QR Code ou clique no link abaixo para validar o
              documento
            </Text>

            {/* Link clicável que exibe apenas "Clique aqui" */}
            <Link src={linkDocument} style={styles.qrCodeLink}>
              Clique aqui para validar
            </Link>

            <Text style={styles.qrCodeInstructions}>
              Este código pode ser usado para verificar a autenticidade e
              integridade do documento através da plataforma Fluxmed
            </Text>
          </View>
        </Page>
      )}
    </Document>
  );
};

export default VetDocumentTemplate;
